// The frame and the type the two public documents are set in.
//
// **One module because they are deliberately identical** (`TermsPage`'s own
// rule): two legal documents linked side by side that are set differently read
// as one of them having been taken less seriously. It was two copies of the
// same component map; on the kit at U9 it is one, and the pages are the
// heading, the loader and the back button.
//
// Styling lives here rather than in the `.mdx` — the split `RulesPage` makes,
// and the reason a translator never has to read a style prop.

import type { MDXComponents } from "mdx/types";

import { Btn, Link, Page, PageColumn, Text, styled } from "./kit";

/** A document's own heading — the one `h1` in the `.mdx`. */
const H1 = styled("h1")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 28,
  lineHeight: 1.15,
  color: theme.kit.color.ink,
  margin: `${theme.kit.space.xl}px 0 ${theme.kit.space.md}px`
}));

const H2 = styled("h2")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 19,
  color: theme.kit.color.brand,
  margin: `${theme.kit.space.xl}px 0 ${theme.kit.space.sm}px`
}));

const P = styled("p")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 14.5,
  lineHeight: 1.7,
  color: theme.kit.color.inkDim,
  margin: `0 0 ${theme.kit.space.md}px`
}));

const LI = styled("li")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 14.5,
  lineHeight: 1.7,
  color: theme.kit.color.inkDim,
  marginBottom: theme.kit.space.xs
}));

const Strong = styled("strong")(({ theme }) => ({ color: theme.kit.color.ink }));

const Code = styled("code")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontSize: 13,
  color: theme.kit.color.cream
}));

/** The bar that goes back, stuck to the top of the scroll. */
const Bar = styled("div")(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: theme.kit.z.chrome,
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.md,
  padding: `${theme.kit.space.sm}px ${theme.kit.space.lg}px`,
  background: `color-mix(in srgb, ${theme.kit.color.ground} 92%, transparent)`,
  backdropFilter: "blur(8px)",
  borderBottom: `2px solid ${theme.kit.color.line}`
}));

/**
 * The component map every public document is rendered through.
 *
 * Extra entries — the privacy notice's `MailUse` / `MailHost` — are spread over
 * this by the page that needs them.
 */
export const DOC_COMPONENTS: MDXComponents = {
  h1: (props) => <H1 {...props} />,
  h2: (props) => <H2 {...props} />,
  p: (props) => <P {...props} />,
  li: (props) => <LI {...props} />,
  strong: (props) => <Strong {...props} />,
  code: (props) => <Code {...props} />,
  a: (props) => <Link {...props} target="_blank" rel="noreferrer" />
};

/**
 * The page around a document: a back bar, a measure, and what to say when the
 * chunk did not load.
 *
 * Saying so beats an empty page, and it matters more here than on the rules: a
 * blank privacy notice reads as not having one.
 */
export function DocumentPage({
  testid,
  heading,
  backTestid,
  onClose,
  failed,
  source,
  children
}: {
  testid: string;
  heading: string;
  backTestid: string;
  onClose(): void;
  failed: boolean;
  /** Where the document lives in the repository, for the failure line. */
  source: string;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <Page data-testid={testid}>
      <Bar>
        <Btn small variant="ghost" data-testid={backTestid} onClick={onClose}>
          ← Back
        </Btn>
        <Text role="heading" size={16}>
          {heading}
        </Text>
      </Bar>
      <PageColumn width={760}>
        {failed && (
          <P>
            This page could not be loaded. It is also readable in the repository at{" "}
            <Code>{source}</Code>.
          </P>
        )}
        {children}
      </PageColumn>
    </Page>
  );
}
